/**
 * Debridement — payload assembly.
 *
 * The single place editable state becomes the PATCH body. Nothing else in the
 * section may construct one; if a field needs to reach .NET it is added here.
 *
 * Field names for Blocks 2 and 3 are the names the API already persists, so
 * those are not a breaking change. The additions are:
 *   prerequisites · complianceFlags · notPerformedReason · painAfter · configVersion
 */

import { DEBRIDEMENT_CONFIG_VERSION } from "./debridementConfig";
import {
  visiblePrerequisites,
  policyFor,
  complianceFlags,
  POLICY_WIRE,
} from "./debridementRules";

export const buildDebridementPayload = ({ state, careSetting, ids }) => {
  const performed = state.isDebridementPerformed === "yes";

  const prerequisites = visiblePrerequisites(careSetting).map((p) => {
    const a = state.prerequisiteAnswers?.[p.key] || {};
    return {
      key: p.key,
      policy: POLICY_WIRE[policyFor(p, careSetting)],
      answer: a.value ?? null,                                  // "yes" | "no" | "na" | null
      value: a.selection ?? null,                               // the picked option, where one exists
      reasonCode: a.value === "no" ? a.reasonCode ?? null : null,
      reasonText: a.value === "no" ? a.reasonText ?? null : null,
    };
  });

  return {
    // provenance — lets the API interpret a record against the client build
    // that produced it, which is the only defence against a frontend-owned
    // option list drifting from what is stored
    configVersion: DEBRIDEMENT_CONFIG_VERSION,
    careSetting,

    // Block 0
    isDebridementPerformed: state.isDebridementPerformed,
    notPerformedReason: performed ? null : state.notPerformedReason || null,

    // Block 1 — keyed collection, so a new prerequisite is a data change and
    // not a schema migration
    prerequisites: performed ? prerequisites : [],
    // complianceFlags: performed ? complianceFlags(careSetting, state.prerequisiteAnswers) : [],

    // Block 2 — existing API field names preserved
    surgicalDebridementProcedure: performed ? state.surgicalDebridementProcedure || "" : "",
    nonExcisionalApproach: performed ? state.nonExcisionalApproach || "" : "",
    depthOfDebridement: performed ? state.depthOfDebridement || "" : "",
    necroticSubcutaneousTissue: performed ? state.necroticSubcutaneousTissue || [] : [],
    necroticTissue: performed ? state.necroticTissue || [] : [],
    notApplicable: [],
    instrumentsUsed: performed ? state.instrumentsUsed || [] : [],
    surfaceAreaOfNecroticSkinTissue: performed ? state.surfaceAreaOfNecroticSkinTissue || "" : "",
    surfaceAreaOfNecroticSubqTissue: performed ? state.surfaceAreaOfNecroticSubqTissue || "" : "",
    techniqueUsed: performed ? state.techniqueUsed || "" : "",
    honeyDressing: null,

    // Block 3
    painAfter: performed && state.painAfter !== "" ? Number(state.painAfter) : null,
    postDebridementBleedingAmount: performed ? state.postDebridementBleedingAmount || "" : "",
    // only meaningful when bleeding occurred
    stoppedBleedingContactPressure:
      performed && state.postDebridementBleedingAmount &&
      state.postDebridementBleedingAmount !== "none"
        ? state.stoppedBleedingContactPressure || ""
        : "",

    ...ids,
  };
};
